// The minimap for the fast client (ui-map-interface.md §8 "Minimap", §3.2).
//
// A 5320 px world at a readable zoom shows maybe a tenth of itself, and
// everything that matters — the front, your base, a contest — happens off
// screen. The chart is a picture of the world, and §1 puts pictures on a
// canvas, so it stays a canvas here too: a SECOND small canvas of `MAP_ART`
// px, between the WebGL canvas and `#ui`, drawn with Canvas 2D.
//
// It is its own canvas rather than a layer of the engine because it is a
// picture of the WHOLE world, drawn at a fixed size in a corner. A layer
// would have to fight the camera for every mark; a 168 px canvas redrawn
// only when something changed costs one `drawImage` and no GPU state.
//
// The two dirty rules of the PixiJS chart are kept exactly (§10.2,
// §10.3): territory is redrawn only on `invalidate()`, and the dynamic
// layer only when `takeDynDirty` says an input changed. On a still screen a
// tick is one identity compare and four number compares.
//
// The chart takes the chrome dock-in with the panels beside it (R182,
// R499). `setDockProgress` is the eased 0..1 `ui/dockin.ts` publishes: the
// panels are CSS transitions and the chart cannot be one, so it reads the
// curve the dock-in sampled and turns it into an offset of its own, and the
// chart arrives from below. `setDockFlash` is the border flash of contact,
// and `setDockRing` the arrival ring of Surface Wake (R826). Each of the
// three is the chart's half of a mark the panels wear in CSS.
//
// The chart does not collapse: `setOpen` shows and hides the canvas at
// once (ui-map-interface.md §3.2).
//
// **The chart is round** (R1801, card 17 b of mockup round 086). It is a
// disc in the frame of the compass rose, and the console gives it the
// square of that disc. The picture is drawn into a circle clip, a press
// outside the circle is no press on the chart (it is the rose press), and
// the frame, the flash and the Surface Wake ring are circles. **While a
// heatmap mode paints the map, the disc shows the heat field in place of
// the territory** (R1801, card 17 a): `setHeat` hands over the field the
// heatmap layer holds, and the territory picture is drawn from it through
// the mode's own ramp (`render/heatmap-ramp.ts`). The disc then shows the
// heat surface alone: no structure dot, no control point and no home ring,
// only the viewport frame of the camera over the field (R1849). The HEAT
// chip of the overlay (`#heatchip`) says that the disc reads heat.

import { BLOCK_RIDGE, cellAt, factionColor, WORLD, WORLD_H } from '../world';
import { channels, rgba } from '../render/color';
import { bucketPaint, HEAT_BUCKETS } from '../render/heatmap-ramp';
import { heatMode } from '../state';
import { HeatmapKind, SiteKind } from '../gen/proto/aquaphobia';
import { clusterBox, MAP_ART, PAD, onCluster } from '../ui/layout';
// The chart's frame wears the chrome accent (R312). The Surface Wake ring
// (R826) takes the radius it starts at, how far it opens and the alpha it
// starts at from the data layer, so the chart and the panels take one
// figure each from one table.
import {
  ACCENT_COLOR as ACCENT,
  CHROME_EDGE_ALPHA as EDGE_ALPHA,
  MINIMAP_DOCK_RING_ALPHA as DOCK_RING_ALPHA,
  MINIMAP_DOCK_RING_R0_PX as DOCK_RING_R0,
  MINIMAP_DOCK_RING_SCALE as DOCK_RING_SCALE,
  MAP_PALETTE, MARK_INKS,
} from '../gen/presentation';
import { clamp01 } from '../render/math';

/** One structure dot, as `SiteLayer.structures()` hands it over. */
export interface MinimapSite { x: number; y: number; faction: number; kind: number }

/** The kept copy of the dynamic layer's inputs, for the dirty compare. */
export interface DynState {
  sites: readonly MinimapSite[] | null;
  x0: number; y0: number; x1: number; y1: number;
  homeX: number | null;
  homeY: number | null;
}

export function newDynState(): DynState {
  return { sites: null, x0: NaN, y0: NaN, x1: NaN, y1: NaN, homeX: null, homeY: null };
}

/**
 * The dirty-flag decision (R131), split out so a test can run it without a
 * canvas. Returns true when any dynamic-layer input changed since the kept
 * copy, and folds the new inputs into `last` when it does. The structures
 * compare is **identity**: `SiteLayer.structures()` returns the same array
 * until a site is added or changes owner, so a changed array means changed
 * content. The viewport and home compares are by value, because the frame
 * loop rebuilds those small objects each frame.
 */
export function takeDynDirty(
  last: DynState,
  sites: readonly MinimapSite[],
  view: { x0: number; y0: number; x1: number; y1: number },
  home: { x: number; y: number } | null,
): boolean {
  const same = last.sites === sites
    && last.x0 === view.x0 && last.y0 === view.y0
    && last.x1 === view.x1 && last.y1 === view.y1
    && (home === null
      ? last.homeX === null
      : last.homeX === home.x && last.homeY === home.y);
  if (same) return false;
  last.sites = sites;
  last.x0 = view.x0; last.y0 = view.y0; last.x1 = view.x1; last.y1 = view.y1;
  last.homeX = home === null ? null : home.x;
  last.homeY = home === null ? null : home.y;
  return true;
}

/** The chart's drawing size in chart px. Where it SITS is the console's business. */
const SIZE = MAP_ART;

/** The chart's own paint, as `render/minimap.ts:185-187` states it: the
 *  `--deep` of the palette (R1534), in the identity of the map (R3296), so
 *  the light chart is paper under the light territory. */
const PANEL_FILL = `rgb(${channels(MAP_PALETTE.deep).join(' ')} / 0.72)`;
/** The home ring and the viewport rect: the white of a mark, a dark core
 *  in the light identity (R3296). */
const HOME_INK = rgba(MARK_INKS.white, 0.9);
const VIEW_INK = rgba(MARK_INKS.white, 0.35);
/** The sovereignty wash on the chart. */
const LAND_ALPHA = 0.32;
/** How bright the snap flash paints the chart's frame at its brightest.
 *  `render/minimap.ts:259` states the same figure. */
const SNAP_ALPHA = 0.85;
/** A structure dot, a control-point ring, the home marker and the viewport. */
const DOT_R = 1.3;
const CP_R = 2.6;
const HOME_R = 2.6;

/**
 * The screen→world mapping behind `hit` and `at`, split out from the
 * drawing so a test can run it without a canvas (the same reason
 * `takeDynDirty` is out here).
 *
 * `box` is the chart as the page sees it: its top-left in screen px, its
 * display scale — the chart is drawn at SIZE and may be shown smaller in
 * the sheet form factor, so screen px are not chart px — and whether it is
 * on screen at all. A hidden chart is never under the pointer.
 *
 * `clamp` is the difference between the two callers: a press must land
 * inside the chart (null outside it), while a drag that started there
 * keeps scrubbing at the edge instead of cutting off. A `round` chart
 * (R1801) takes a press inside its circle only, and a drag still clamps to
 * the square round it, so a scrub reaches a corner of the world.
 */
export function pick(
  px: number,
  py: number,
  box: { x: number; y: number; scale: number; visible: boolean; round?: boolean },
  clamp: boolean,
): { x: number; y: number } | null {
  if (!box.visible || box.scale <= 0) return null;
  let x = (px - box.x) / box.scale;
  let y = (py - box.y) / box.scale;
  if (clamp) {
    x = Math.min(SIZE, Math.max(0, x));
    y = Math.min(SIZE, Math.max(0, y));
  } else if (x < 0 || y < 0 || x > SIZE || y > SIZE) {
    return null;
  } else if (box.round && Math.hypot(x - SIZE / 2, y - SIZE / 2) > SIZE / 2) {
    return null;
  }
  const s = mapScale();
  return { x: x / s, y: y / s };
}

/**
 * The chart's world→chart scale. The chart panel is a square and the world
 * is a rectangle (R395), so the longer world edge sets the scale and the
 * shorter one letterboxes: a wide map draws as a band across the middle
 * rather than stretched.
 */
export function mapScale(): number {
  return SIZE / Math.max(WORLD, WORLD_H);
}

/** What the chart needs of the page, so a test can hand it a stub. */
export interface MinimapHost {
  /** Where the chart sits and how big its slot is, in screen px. */
  box(): { x: number; y: number; w: number; h: number; visible: boolean };
  /** Subscribes to the console's recompute. */
  onLayout(fn: () => void): void;
  /** Puts the canvas on the page, under `#ui`. */
  mount(canvas: HTMLCanvasElement): void;
  /**
   * The 2D context of one canvas, or null where there is none.
   *
   * It is a seam and not a call to `getContext` in place, because jsdom
   * has no 2D context and answers the call with a console error. A test
   * hands over a host that answers null, which is the same thing a browser
   * with the canvas switched off answers, and the drawing half returns at
   * once either way.
   */
  context(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null;
}

/** The real page: the console contract of `ui/layout.ts` and the DOM. */
export function pageHost(): MinimapHost {
  return {
    box: () => clusterBox('map'),
    onLayout: (fn) => onCluster(fn),
    mount(canvas) {
      // Between the WebGL canvas and the chrome: the chart covers the map
      // and the dock covers the chart.
      const ui = document.getElementById('ui');
      if (ui?.parentNode) ui.parentNode.insertBefore(canvas, ui);
      else document.body.appendChild(canvas);
    },
    context: (canvas) => canvas.getContext('2d'),
  };
}

export class Minimap {
  /** The visible chart. The wiring reads it only to dispose of it. */
  readonly canvas: HTMLCanvasElement;

  private readonly ctx: CanvasRenderingContext2D | null;
  /** The territory picture, redrawn only on `invalidate()`. */
  private readonly land: HTMLCanvasElement;
  private readonly landCtx: CanvasRenderingContext2D | null;

  private owners: Uint8Array | null = null;
  private scale = mapScale();
  private dirty = true;
  private dynState = newDynState();
  private open = true;
  /** The slot the console last gave, in screen px. */
  private slot = { x: 0, y: 0, w: 0, h: 0, visible: false };
  /** The chrome dock-in's eased travel: 0 outside the viewport, 1 docked. */
  private dockProgress = 1;
  /** The flash of contact on the chart's frame, already scaled. */
  private snapAlpha = 0;
  /** How far the Surface Wake ring has opened, 0..1. 0 draws none. */
  private ringK = 0;
  /** A compose the flash asked for, which no dirty rule of its own covers. */
  private recompose = false;
  /** The heatmap mode that paints, its field and the blocked mask (R1801).
   *  NONE draws the territory. */
  private heatKind: HeatmapKind = HeatmapKind.HEATMAP_KIND_NONE;
  private heatCells: Uint8Array | null = null;
  private blocked: Uint8Array | null = null;

  constructor(private readonly host: MinimapHost = pageHost()) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
    this.canvas.id = 'fast-minimap';
    // The chart takes no pointer: `hit()` and `at()` run in the main
    // canvas's own handlers, so the map keeps one pointer path.
    this.canvas.style.cssText = [
      'position:fixed', 'left:0', 'top:0', 'z-index:5',
      'pointer-events:none', 'display:none',
    ].join(';');
    this.ctx = host.context(this.canvas);
    this.land = document.createElement('canvas');
    this.land.width = SIZE;
    this.land.height = SIZE;
    this.landCtx = host.context(this.land);
    host.mount(this.canvas);
    this.layout();
    // The console recomputes on a resize, a rotation and a change of the
    // dock's height, and says so once. Nothing here is on a timer, and the
    // chart never measures the corner for itself.
    host.onLayout(() => this.layout());
  }

  /** Is the chart asked for? False while it is minimized. */
  get isOpen(): boolean { return this.open; }

  /**
   * The MINIMAP tile of the VIEW panel (R1747), and the chart's own `–`.
   * Read from the frame loop every frame, so it returns
   * without work when nothing changed.
   *
   * A minimized chart is also out of every pointer path: `hit` and `at`
   * both go through `pick`, which answers null for a box that is not
   * visible, so a click, a drag and a tap all miss the square.
   */
  setOpen(on: boolean): void {
    if (on === this.open) return;
    this.open = on;
    this.layout();
  }

  /**
   * The heat field in the disc (R1801, card 17 a). The frame loop hands
   * over the mode, the field the heatmap layer holds and the blocked mask
   * on every frame, so the three compares are by identity: a new field is
   * a new array. A change redraws the picture once; NONE, or a mode whose
   * field has not arrived yet, draws the territory.
   */
  setHeat(kind: HeatmapKind, cells: Uint8Array | null, blocked: Uint8Array | null): void {
    if (kind === this.heatKind && cells === this.heatCells && blocked === this.blocked) return;
    this.heatKind = kind;
    this.heatCells = cells;
    this.blocked = blocked;
    this.dirty = true;
  }

  /** True while the disc shows the heat field and not the territory. */
  get showsHeat(): boolean {
    return this.heatKind !== HeatmapKind.HEATMAP_KIND_NONE && this.heatCells !== null;
  }

  setOwners(owners: Uint8Array): void {
    this.owners = owners;
    this.dirty = true;
  }

  /** Called when a cell flips; the redraw is cheap enough to do wholesale. */
  invalidate(): void { this.dirty = true; }

  /**
   * How far through the chrome dock-in the arrival is (R182).
   *
   * The figure is `ChromeDockIn.progress`, which is the same eased curve the
   * panels ride, sampled once there rather than guessed twice here: an eased
   * approach and a back-out approach diverge most in the last 150 ms, which
   * is exactly where a mismatch shows. The curve overshoots its dock line,
   * so the range is 0 to 1.2.
   */
  setDockProgress(p: number): void {
    const clamped = Math.min(1.2, Math.max(0, p));
    if (clamped === this.dockProgress) return;
    this.dockProgress = clamped;
    this.layout();
  }

  /**
   * The flash of contact as the chart lands, on the keyframes the CSS uses:
   * nothing until 62% of the travel, brightest at 72%, gone by the end
   * (`snapFlash` in `ui/dockin.ts`).
   *
   * It is a second stroke of the chart's own frame and not a `box-shadow`:
   * an inset shadow paints under the bitmap. A call that does not move the
   * figure marks nothing, so a still chart stays still.
   */
  setDockFlash(k: number): void {
    const v = clamp01(k) * SNAP_ALPHA;
    if (v === this.snapAlpha) return;
    this.snapAlpha = v;
    this.recompose = true;
  }

  /**
   * The sonar ring the Surface Wake arrival opens under the chart, 0..1
   * (R826, `ChromeDockIn.ring`).
   *
   * The panels drag a leading edge of the accent, which is a CSS
   * pseudo-element; the chart is a canvas, so its own arrival mark is one
   * stroked circle that opens and fades. Every other dock-in value hands
   * over 0 and nothing is drawn. It follows the flash's rule exactly: a
   * call that does not move the figure marks nothing.
   */
  setDockRing(k: number): void {
    const v = clamp01(k);
    if (v === this.ringK) return;
    this.ringK = v;
    this.recompose = true;
  }

  /**
   * Takes the world rectangle's new size (R261, R395). The chart is built
   * at boot, before any socket exists, so `scale` starts at the default
   * world's figure; the wiring calls this when the Hello gave a different
   * grid.
   */
  rescale(): void {
    this.scale = mapScale();
    this.dirty = true;
  }

  /**
   * Permanent information only (R45): territory, the fixed structures, your
   * home, and the viewport rect. Nothing sonar-derived — the minimap is a
   * chart, not a tactical display, so no fleet dots and no contest markers
   * that would leak or duplicate the sonar picture.
   */
  tick(
    sites: readonly MinimapSite[],
    view: { x0: number; y0: number; x1: number; y1: number },
    home: { x: number; y: number } | null,
  ): void {
    // Nothing for a chart nobody is looking at (§10.3). Nothing is folded
    // in on the way out, so the first tick after it comes back redraws.
    if (!this.shown()) return;
    let compose = false;
    if (this.dirty && (this.owners || this.showsHeat)) {
      this.dirty = false;
      if (this.showsHeat) this.redrawHeat();
      else this.redrawTerritory();
      compose = true;
    }
    // R131: redraw the dynamic layer only when an input changed. On a still
    // screen this makes tick() a compare and nothing else.
    if (takeDynDirty(this.dynState, sites, view, home)) compose = true;
    // The snap flash is on none of those inputs: it moves on its own clock
    // for the one second the dock-in lasts, and it is over after that.
    if (this.recompose) { this.recompose = false; compose = true; }
    if (compose) this.compose(sites, view, home);
  }

  /** Maps a click inside the chart to a world position, or null. */
  hit(px: number, py: number): { x: number; y: number } | null {
    return pick(px, py, this.box(), false);
  }

  /**
   * Like `hit`, but clamped into the chart: a drag that started on the
   * chart keeps scrubbing at the edge instead of cutting off the moment the
   * pointer strays outside it. Only ever called for a drag already claimed,
   * so a hidden chart cannot reach it; it answers the centre of the world
   * rectangle if one ever did.
   */
  at(px: number, py: number): { x: number; y: number } {
    return pick(px, py, this.box(), true) ?? { x: WORLD / 2, y: WORLD_H / 2 };
  }

  /** Takes the canvas off the page. */
  dispose(): void {
    this.canvas.remove();
  }

  // -- geometry --------------------------------------------------------------

  /** The chart as the pointer sees it. */
  box(): { x: number; y: number; scale: number; visible: boolean; round: boolean } {
    return {
      x: this.slot.x,
      y: this.slot.y,
      scale: this.slot.w / SIZE,
      visible: this.shown(),
      round: true,
    };
  }

  private shown(): boolean {
    return this.open && this.slot.w > 0;
  }

  /**
   * Where the chart is. Every figure comes from the console contract
   * (`ui/layout.ts`), which knows what the rose and the legend are
   * doing; this file states no position of its own.
   */
  private layout(): void {
    const b = this.host.box();
    this.slot = { x: b.x, y: b.y, w: b.w, h: b.h, visible: b.visible };
    const s = this.canvas.style;
    if (!this.shown()) {
      s.display = 'none';
      return;
    }
    s.display = 'block';
    s.left = `${b.x}px`;
    // The chrome dock-in (R182). The chart stands in the console (R1801),
    // so it arrives from below with the panels; a slot in the upper half
    // of the screen arrives from above. Each way starts past the edge of
    // the screen: a telephone's slot stands over the dock, so one slot of
    // travel left the chart on the black of the arrival's hold.
    const rest = 1 - this.dockProgress;
    const vh = typeof window === 'undefined' ? 0 : window.innerHeight;
    const fromTop = b.y + b.h / 2 < vh / 2;
    s.top = fromTop
      ? `${b.y - rest * (b.y + b.h + PAD)}px`
      : `${b.y + rest * Math.max(b.h + PAD * 2, vh - b.y + PAD)}px`;
    // The chart is drawn at SIZE and shown at the slot's width, which is
    // the same picture the PixiJS container's `scale.set(b.w / SIZE)` gave.
    s.width = `${b.w}px`;
    s.height = `${b.w}px`;
  }

  // -- drawing ---------------------------------------------------------------

  /**
   * The territory picture.
   *
   * The PixiJS chart draws one small rectangle per owned cell — about 20 k
   * of them — and fills them in five batches. This one walks the 168²
   * pixels of the chart instead and asks `cellAt` which hex each pixel
   * belongs to: 28 k integer steps, no gaps between neighbours, and the
   * cells come out as the hexes they are rather than as squares. It is the
   * one deliberate difference from the PixiJS chart, and it is the reason
   * the plan named the walk.
   */
  private redrawTerritory(): void {
    const ctx = this.landCtx;
    const owners = this.owners;
    if (!ctx || !owners) return;
    // `putImageData` replaces every pixel of the picture, so no clear runs first.
    const img = ctx.createImageData(SIZE, SIZE);
    const d = img.data;
    const inv = 1 / this.scale;
    const a = Math.round(LAND_ALPHA * 255);
    for (let py = 0; py < SIZE; py++) {
      const wy = (py + 0.5) * inv;
      const row = py * SIZE * 4;
      for (let px = 0; px < SIZE; px++) {
        const i = cellAt((px + 0.5) * inv, wy);
        if (i < 0 || i >= owners.length) continue;
        const o = owners[i];
        if (o === 0) continue;
        const c = factionColor(o);
        const at = row + px * 4;
        d[at] = (c >> 16) & 0xff;
        d[at + 1] = (c >> 8) & 0xff;
        d[at + 2] = c & 0xff;
        d[at + 3] = a;
      }
    }
    ctx.putImageData(img, 0, 0);
  }

  /**
   * The heat field in place of the territory (R1801, card 17 a): the same
   * walk of the chart's pixels, each painted with the bucket of its cell
   * in the mode's own ramp, so the disc and the map read one field. A
   * cell of intensity 0 and a ridge stay clear, as on the map (R103).
   */
  private redrawHeat(): void {
    const ctx = this.landCtx;
    const cells = this.heatCells;
    if (!ctx || !cells) return;
    const mode = heatMode(this.heatKind);
    // The ramp of the mode as one table: four bytes a bucket.
    const lut = new Uint8ClampedArray(HEAT_BUCKETS * 4);
    for (let b = 0; b < HEAT_BUCKETS; b++) {
      const { color, alpha } = bucketPaint(mode, b);
      lut[b * 4] = (color >> 16) & 0xff;
      lut[b * 4 + 1] = (color >> 8) & 0xff;
      lut[b * 4 + 2] = color & 0xff;
      lut[b * 4 + 3] = Math.round(alpha * 255);
    }
    const blocked = this.blocked;
    const img = ctx.createImageData(SIZE, SIZE);
    const d = img.data;
    const inv = 1 / this.scale;
    for (let py = 0; py < SIZE; py++) {
      const wy = (py + 0.5) * inv;
      const row = py * SIZE * 4;
      for (let px = 0; px < SIZE; px++) {
        const i = cellAt((px + 0.5) * inv, wy);
        if (i < 0 || i >= cells.length) continue;
        const v = cells[i];
        if (v === 0 || (blocked && blocked[i] === BLOCK_RIDGE)) continue;
        let b = (((v - 1) * HEAT_BUCKETS) / 255) | 0;
        if (b >= HEAT_BUCKETS) b = HEAT_BUCKETS - 1;
        const at = row + px * 4;
        d[at] = lut[b * 4];
        d[at + 1] = lut[b * 4 + 1];
        d[at + 2] = lut[b * 4 + 2];
        d[at + 3] = lut[b * 4 + 3];
      }
    }
    ctx.putImageData(img, 0, 0);
  }

  /**
   * One composite of the whole chart: the panel, the territory picture, the
   * structures, the home marker and the viewport rect. It runs only on a
   * frame where the territory or a dynamic input changed.
   */
  private compose(
    sites: readonly MinimapSite[],
    view: { x0: number; y0: number; x1: number; y1: number },
    home: { x: number; y: number } | null,
  ): void {
    const g = this.ctx;
    if (!g) return;
    g.clearRect(0, 0, SIZE, SIZE);
    // The disc (R1801): every mark of the chart stands inside the circle.
    const C = SIZE / 2;
    g.save();
    g.beginPath();
    g.arc(C, C, C, 0, Math.PI * 2);
    g.clip();
    g.fillStyle = PANEL_FILL;
    g.fillRect(0, 0, SIZE, SIZE);
    g.drawImage(this.land, 0, 0);

    // Structures: faction installations and commander holds as dots,
    // control points ringed in their holder's colour. Every stroke of the
    // chart is 1 px wide but the home marker's. The heat surface stands
    // alone (R1849, card 17 a): it draws no structure and no home ring.
    const s = this.scale;
    const heat = this.showsHeat;
    g.lineWidth = 1;
    // `factionColor` answers the neutral ink for faction 0.
    if (!heat) {
      for (const st of sites) {
        const colour = factionColor(st.faction);
        const cp = st.kind === SiteKind.SITE_KIND_CONTROL_POINT;
        g.beginPath();
        g.arc(st.x * s, st.y * s, cp ? CP_R : DOT_R, 0, Math.PI * 2);
        if (cp) {
          g.strokeStyle = rgba(colour, 0.9);
          g.stroke();
        } else {
          g.fillStyle = rgba(colour, 0.9);
          g.fill();
        }
      }
    }

    if (home && !heat) {
      g.beginPath();
      g.arc(home.x * s, home.y * s, HOME_R, 0, Math.PI * 2);
      g.lineWidth = 1.4;
      g.strokeStyle = HOME_INK;
      g.stroke();
      g.lineWidth = 1;
    }

    g.strokeStyle = VIEW_INK;
    g.strokeRect(
      view.x0 * s, view.y0 * s,
      (view.x1 - view.x0) * s, (view.y1 - view.y0) * s,
    );

    g.restore();

    // The chart's own frame, over everything it holds: one circle in the
    // edge ink of the chrome (R1801).
    g.beginPath();
    g.arc(C, C, C - 0.5, 0, Math.PI * 2);
    g.strokeStyle = rgba(ACCENT, EDGE_ALPHA);
    g.stroke();

    // The flash of contact, over the frame and on the same line (R182).
    if (this.snapAlpha > 0) {
      g.strokeStyle = rgba(ACCENT, this.snapAlpha);
      g.stroke();
    }

    // The Surface Wake ring (R826): one circle that opens from the chart's
    // centre and fades as it goes. It is the chart's half of the arrival
    // mark the panels wear as a leading edge.
    const k = this.ringK;
    if (k > 0 && k < 1) {
      g.beginPath();
      g.arc(C, C, Math.min(C - 0.5, k * DOCK_RING_SCALE * DOCK_RING_R0), 0, Math.PI * 2);
      g.strokeStyle = rgba(ACCENT, (1 - k) * DOCK_RING_ALPHA);
      g.stroke();
    }
  }
}
